import { useState, useRef, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, FileText, User, Bot, Loader2, Code, MessageSquare, Send, Save, Trash2, Eraser } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { papersApi } from '@/services/api/papers'
import { showSuccessToast } from '@/utils/error-handler'
import { useAI } from '@/hooks/useAI'
import { showErrorToast } from '@/utils/error-handler'
import MarkdownRenderer from '@/components/ui/markdown'
import MonacoEditor from '@/components/editor/MonacoEditor'
import type { AIMessage } from '@/services/ai/ai-service'

interface PaperPayload {
  title?: string
  savedAt?: string
  context?: {
    resultType?: string
    scriptDescription?: string
    scriptCode?: string
    datasetInfo?: Record<string, unknown>
  }
  explanation?: string
  chat?: Array<{ role: string; content: string; timestamp?: string; intent?: string }>
  dataPreview?: unknown
}

function formatDate(iso: string | undefined) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { dateStyle: 'medium' }) + ' ' + d.toLocaleTimeString(undefined, { timeStyle: 'short' })
}

function buildPaperContextPrompt(payload: PaperPayload, paperTitle: string): string {
  const ctx = payload.context ?? {}
  const datasetInfo = (ctx.datasetInfo ?? {}) as Record<string, unknown>
  const parts: string[] = [
    `You are helping the user discuss a saved analysis paper titled "${paperTitle}".`,
    'The paper contains the following:',
  ]
  if (ctx.resultType) parts.push(`- Result type: ${ctx.resultType}`)
  if (ctx.scriptDescription) parts.push(`- Script: ${ctx.scriptDescription}`)
  if (datasetInfo.symbol) parts.push(`- Symbol: ${datasetInfo.symbol}`)
  if (datasetInfo.timeframe) parts.push(`- Timeframe: ${datasetInfo.timeframe}`)
  if (datasetInfo.timezone) parts.push(`- Timezone: ${datasetInfo.timezone}`)
  if (payload.explanation) {
    const excerpt = payload.explanation.slice(0, 2000) + (payload.explanation.length > 2000 ? '...' : '')
    parts.push(`\nAI Explanation from the paper:\n${excerpt}`)
  }
  if (payload.dataPreview != null) {
    const dp = payload.dataPreview as { total?: number; columns?: string[] }
    if (typeof dp === 'object' && (dp.total != null || dp.columns)) {
      parts.push(`\nData preview: ${dp.total ?? 'N'} rows, columns: ${(dp.columns ?? []).join(', ') || 'N/A'}.`)
    } else {
      parts.push('\nThe paper includes a data preview (stats or object).')
    }
  }
  if (ctx.scriptCode) {
    const codeExcerpt = String(ctx.scriptCode).slice(0, 1500) + (String(ctx.scriptCode).length > 1500 ? '\n...' : '')
    parts.push(`\nScript (DSL) excerpt:\n\`\`\`\n${codeExcerpt}\n\`\`\``)
  }
  parts.push('\nAnswer questions about this paper concisely. If the user asks for code or details not in the excerpt, suggest opening the paper or using "Use code" to edit the script.')
  return parts.join('\n')
}

export default function PaperDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [chatMessages, setChatMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([])
  const [chatInput, setChatInput] = useState('')
  const chatEndRef = useRef<HTMLDivElement>(null)

  const { callAIChat, isLoading: isChatLoading, isConfigured: isAIConfigured } = useAI({
    onError: (err) => showErrorToast(err, 'AI chat failed'),
  })

  const queryClient = useQueryClient()
  const { data: paper, isLoading, error } = useQuery({
    queryKey: ['paper', id],
    queryFn: () => papersApi.get(id!),
    enabled: !!id,
  })

  const saveChatMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => papersApi.update(id!, { payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paper', id] })
      showSuccessToast('Conversation saved to paper')
    },
    onError: (err) => showErrorToast(err, 'Failed to save conversation'),
  })

  const hasSeededChatRef = useRef(false)
  const prevPaperIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!paper?.id) return
    if (prevPaperIdRef.current !== paper.id) {
      prevPaperIdRef.current = paper.id
      hasSeededChatRef.current = false
    }
    if (hasSeededChatRef.current) return
    const payload = paper.payload as Record<string, unknown> | undefined
    const chat = payload?.chat
    if (!Array.isArray(chat) || chat.length === 0) return
    hasSeededChatRef.current = true
    setChatMessages(
      chat.map((m: { role: string; content: string }) => ({
        role: (m.role === 'user' ? 'user' : 'assistant') as 'user' | 'assistant',
        content: m.content,
      }))
    )
  }, [paper])

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages])

  if (!id) {
    navigate('/papers')
    return null
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !paper) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-destructive">Paper not found.</p>
        <Link to="/papers">
          <Button variant="outline" className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Papers
          </Button>
        </Link>
      </div>
    )
  }

  const payload = (paper.payload || {}) as PaperPayload
  const ctx = payload.context ?? {}
  const datasetInfo = (ctx.datasetInfo ?? {}) as Record<string, unknown>

  const handleChatSend = async () => {
    const text = chatInput.trim()
    if (!text || !isAIConfigured) return
    setChatInput('')
    const userMessage = { role: 'user' as const, content: text }
    setChatMessages((prev) => [...prev, userMessage])
    const systemPrompt = buildPaperContextPrompt(payload, (payload.title ?? paper.title) || 'Paper')
    const messages: AIMessage[] = [
      { role: 'system', content: systemPrompt },
      ...chatMessages.map((m) => ({ role: m.role, content: m.content })),
      { role: 'user', content: text },
    ]
    const response = await callAIChat(messages, { temperature: 0.3, maxTokens: 1500 })
    if (response) {
      setChatMessages((prev) => [...prev, { role: 'assistant', content: response }])
    }
  }

  const handleSaveConversation = () => {
    const updatedPayload: Record<string, unknown> = { ...(paper.payload as Record<string, unknown>) }
    updatedPayload.chat = chatMessages.map((m) => ({
      role: m.role,
      content: m.content,
      timestamp: new Date().toISOString(),
    }))
    saveChatMutation.mutate(updatedPayload)
  }

  const handleDeleteMessage = (index: number) => {
    setChatMessages((prev) => prev.filter((_, i) => i !== index))
  }

  const handleClearChat = () => {
    setChatMessages([])
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/papers">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Papers
          </Button>
        </Link>
      </div>

      <header className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-7 h-7" />
            {payload.title ?? paper.title}
          </h1>
          <p className="text-muted-foreground mt-1">
            Saved {formatDate(payload.savedAt ?? paper.createdAt)}
          </p>
        </div>
        {ctx.scriptCode && (
          <Button
            onClick={() =>
              navigate('/scripts/new', {
                state: {
                  initialDslCode: ctx.scriptCode,
                  initialName: ((payload.title ?? paper.title) || '').trim() || undefined,
                },
              })
            }
            className="shrink-0"
          >
            <Code className="w-4 h-4 mr-2" />
            Use code
          </Button>
        )}
      </header>

      <div className="space-y-6">
        {/* Context */}
        {Boolean(ctx.resultType ?? ctx.scriptDescription ?? datasetInfo.symbol ?? datasetInfo.timeframe) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Context</CardTitle>
              <CardDescription>Script and dataset used for this analysis</CardDescription>
            </CardHeader>
            <CardContent className="text-sm space-y-1">
              {ctx.resultType != null && <p><strong>Result type:</strong> {String(ctx.resultType)}</p>}
              {ctx.scriptDescription != null && <p><strong>Script:</strong> {String(ctx.scriptDescription)}</p>}
              {datasetInfo.symbol != null && <p><strong>Symbol:</strong> {String(datasetInfo.symbol)}</p>}
              {datasetInfo.timeframe != null && <p><strong>Timeframe:</strong> {String(datasetInfo.timeframe)}</p>}
              {datasetInfo.timezone != null && <p><strong>Timezone:</strong> {String(datasetInfo.timezone)}</p>}
            </CardContent>
          </Card>
        )}

        {/* AI Explanation */}
        {payload.explanation && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">AI Explanation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-muted/50 rounded-lg">
                <MarkdownRenderer content={payload.explanation} className="text-sm" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Chat */}
        {payload.chat && payload.chat.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Chat</CardTitle>
              <CardDescription>Conversation with the AI assistant</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {payload.chat.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                  >
                    <div className={`flex-shrink-0 w-6 h-6 rounded flex items-center justify-center text-xs ${
                      msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted border'
                    }`}>
                      {msg.role === 'user' ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                    </div>
                    <div className={`flex-1 max-w-[85%] rounded p-2 text-xs ${
                      msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted border'
                    }`}>
                      <MarkdownRenderer content={msg.content} className="text-xs" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Data Preview */}
        {payload.dataPreview != null && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Data Preview</CardTitle>
              <CardDescription>Sample of the results data</CardDescription>
            </CardHeader>
            <CardContent>
              {typeof payload.dataPreview === 'object' &&
               payload.dataPreview !== null &&
               'rows' in payload.dataPreview &&
               Array.isArray((payload.dataPreview as { rows?: unknown[] }).rows) ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="border-b">
                        {((payload.dataPreview as { columns?: string[] }).columns ?? []).map((col) => (
                          <th key={col} className="text-left p-2 font-medium">
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {((payload.dataPreview as { rows?: Record<string, unknown>[] }).rows ?? []).slice(0, 30).map((row, i) => (
                        <tr key={i} className="border-b border-border/50">
                          {((payload.dataPreview as { columns?: string[] }).columns ?? []).map((col) => (
                            <td key={col} className="p-2">
                              {String((row as Record<string, unknown>)[col] ?? '')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(payload.dataPreview as unknown as { total?: number }).total != null && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Showing up to 30 of {(payload.dataPreview as unknown as { total: number }).total} rows
                    </p>
                  )}
                </div>
              ) : (
                <pre className="text-xs p-4 bg-muted rounded-lg overflow-x-auto max-h-[400px] overflow-y-auto">
                  {JSON.stringify(payload.dataPreview, null, 2)}
                </pre>
              )}
            </CardContent>
          </Card>
        )}

        {/* Script (DSL) */}
        {ctx.scriptCode && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base">Script (DSL)</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  navigate('/scripts/new', {
                    state: {
                      initialDslCode: ctx.scriptCode,
                      initialName: ((payload.title ?? paper.title) || '').trim() || undefined,
                    },
                  })
                }
              >
                <Code className="w-4 h-4 mr-2" />
                Use code
              </Button>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg overflow-hidden border">
                <MonacoEditor
                  value={String(ctx.scriptCode)}
                  onChange={() => {}}
                  readOnly
                  height="300px"
                  language="quantquack-dsl"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Chat with AI */}
        {isAIConfigured && (
          <Card>
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Chat with AI
                </CardTitle>
                <CardDescription>
                  Ask questions about this paper. The AI has context from the explanation, script, and data.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearChat}
                  disabled={chatMessages.length === 0}
                  title="Clear chat"
                >
                  <Eraser className="w-4 h-4 mr-2" />
                  Clear chat
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSaveConversation}
                  disabled={saveChatMutation.isPending}
                  title="Save current conversation to paper (including empty after Clear chat)"
                >
                  {saveChatMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save conversation
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg flex flex-col" style={{ height: '360px' }}>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {chatMessages.length === 0 && (
                    <div className="text-center text-sm text-muted-foreground py-6">
                      <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>Ask about the analysis, methodology, or how to use the script.</p>
                    </div>
                  )}
                  {chatMessages.map((msg, i) => (
                    <div
                      key={i}
                      className={`group flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                    >
                      <div
                        className={`flex-shrink-0 w-6 h-6 rounded flex items-center justify-center text-xs ${
                          msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted border'
                        }`}
                      >
                        {msg.role === 'user' ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                      </div>
                      <div
                        className={`flex-1 max-w-[85%] min-w-0 rounded p-2 text-xs flex items-start gap-2 ${
                          msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted border'
                        }`}
                      >
                        <span className="flex-1 min-w-0">
                          <MarkdownRenderer content={msg.content} className="text-xs" />
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={`h-6 w-6 shrink-0 opacity-60 hover:opacity-100 ${
                            msg.role === 'user' ? 'text-primary-foreground hover:bg-primary-foreground/20' : 'text-muted-foreground'
                          }`}
                          onClick={() => handleDeleteMessage(i)}
                          title="Delete message"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {isChatLoading && (
                    <div className="flex gap-2 flex-row">
                      <div className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center bg-muted border">
                        <Bot className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 rounded p-2 text-xs bg-muted border flex items-center gap-2">
                        <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                        <span className="text-muted-foreground">Thinking…</span>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
                <div className="p-2 border-t bg-muted/20 flex-shrink-0">
                  <div className="flex gap-2 items-end">
                    <Textarea
                      placeholder="Ask about this paper…"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          handleChatSend()
                        }
                      }}
                      className="min-h-[52px] max-h-[120px] resize-none text-xs"
                      rows={2}
                    />
                    <Button
                      onClick={handleChatSend}
                      disabled={!chatInput.trim() || isChatLoading}
                      size="sm"
                      className="h-[52px] shrink-0"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
